<script setup lang="ts">
// kit-exempt: settings host — binds declarations, scopes and stores to NxSettingsList; its own UI goes only into the list's slots with kit parts (I-2510, I-3022)
/**
 * The one settings list (I-2510): every declared setting — the shell's
 * catalog of what the apps declare — on one layer of the account config
 * chain, the scope. „Bearbeiten für" switches between the scopes the host
 * hands in (the profile, an appearance, a company, a tenant …); every scope
 * shows the same rows: the layer's own value, else the inherited one and who
 * gives it, a field an earlier layer locked read-only, „Zurücksetzen" and —
 * on a tenant's or a company's layer — the lock.
 *
 * The rows are the kit's (`NxSettingsList`); reading and writing is the
 * scope's (`ConnectSettingsScope.store`). Hosts: the shell's settings page,
 * the look designer (its appearance only) and the admin app's panels.
 * kit-exempt: binds the settings catalog to NxSettingsList and forwards host slots
 *
 * A host may put editors of its own before and after the declared sections
 * (`leadingSections`, `trailingSections`: the kit's custom sections, rendered
 * through the `content` slot) — the look designer's colours, type, shape and
 * density first, its tokens last — so one list, one navigation and one
 * search cover everything the host edits.
 */
import { computed, inject, useId, watch } from "vue";
import { routerKey } from "vue-router";
import {
  NxBanner,
  NxButton,
  NxSingleSelect,
  NxSettingsField,
  NxSettingsList,
  i18nInjectionKey,
  type NxSettingsListSection,
} from "@blpde/nx-ui";
import type { AccountConfigLayerRef } from "../config/layerView";
import {
  connectSettingsStoredValue,
  connectSettingsLockField,
} from "../config/settingsDeclaration";
import {
  useConnectSettingsCatalog,
  type ConnectSettingsFieldView,
  type ConnectSettingsSectionView,
} from "./catalog";
import {
  connectSettingsListSections,
  connectSettingsScopeSections,
} from "./listItems";
import type { ConnectSettingsScope } from "./scope";
import type { DriveFolderSelection } from "../drive/types";
import DriveFolderSetting from "./DriveFolderSetting.vue";

const props = withDefaults(
  defineProps<{
    /** The layers the list can edit, in the order „Bearbeiten für" offers them. */
    scopes: readonly ConnectSettingsScope[];
    /** Offer this already selected Drive folder to the folder settings. */
    folder?: DriveFolderSelection;
    /** The sections to show; default: every declared one (the shell's catalog). */
    sections?: readonly ConnectSettingsSectionView[];
    /** A narrow host (the look designer's panel). */
    compact?: boolean;
    /** Kit presentation: the complete list or a searchable editor for one setting. */
    presentation?: "list" | "picker";
    itemLabel?: string;
    /** Control height: `s` on pages, `xs` in narrow inspectors. */
    height?: "xs" | "s";
    /** Show which scope is edited even when there is only one. */
    showScope?: boolean;
    /** The host's translated caption for the edited scope. */
    scopeLabel?: string;
    /** The host's own sections before the declared ones (`custom`, see `content`). */
    leadingSections?: readonly NxSettingsListSection[];
    /** The host's own sections after the declared ones (`custom`, see `content`). */
    trailingSections?: readonly NxSettingsListSection[];
    /** The search field's placeholder (default: the kit's). */
    searchPlaceholder?: string;
  }>(),
  {
    folder: undefined,
    sections: undefined,
    compact: false,
    presentation: "list",
    itemLabel: undefined,
    height: "s",
    showScope: false,
    scopeLabel: undefined,
    leadingSections: () => [],
    trailingSections: () => [],
    searchPlaceholder: undefined,
  },
);

defineSlots<{
  /** Below the list's own notes (read-only, an error) — a note of the host. */
  notice?: () => unknown;
  /** The content of a host's own section; `topics`: its parts that match the search, `null` without one. */
  content?: (props: {
    section: NxSettingsListSection;
    topics: readonly string[] | null;
  }) => unknown;
  /** After the rows of the open section — what the host adds to it (a design's pages of an app). */
  "section-end"?: (props: { section: NxSettingsListSection }) => unknown;
}>();

/** The edited scope's id; default: the first scope that can be picked. */
const scope = defineModel<string | undefined>("scope", { default: undefined });
const section = defineModel<string | undefined>("section", { default: undefined });
const search = defineModel<string>("search", { default: "" });
const changedOnly = defineModel<boolean>("changedOnly", { default: false });

// A link of a row or a section goes through the shell's router; a host
// without one (a harness) shows the links without following them.
const router = inject(routerKey, null);
const i18n = inject(i18nInjectionKey, null);
const switchId = useId();

function t(key: string, params?: Record<string, unknown>): string {
  const id = `settingsList.${key}`;
  if (!i18n) return id;
  return params ? i18n.t(id, params) : i18n.t(id);
}

const catalog = useConnectSettingsCatalog();
void catalog.load();

const declared = computed(() => props.sections ?? catalog.sections.value);

const active = computed<ConnectSettingsScope | undefined>(
  () =>
    props.scopes.find((item) => item.id === scope.value && !item.disabled) ??
    props.scopes.find((item) => !item.disabled),
);

const visible = computed(() =>
  connectSettingsScopeSections(declared.value, active.value),
);

const fields = computed(() => {
  const out = new Map<string, ConnectSettingsFieldView>();
  for (const item of visible.value) {
    for (const field of item.fields) out.set(field.id, field);
  }
  return out;
});

const keys = computed(() => [
  ...new Set([...fields.value.values()].map((field) => field.key)),
]);

// The edited layer of every key the list shows: read when the scope or the
// keys change (a store keeps what it read already).
watch(
  [active, () => keys.value.join(",")],
  ([current]) => {
    if (current && keys.value.length) void current.store.load(keys.value);
  },
  { immediate: true },
);

/** A layer as the source of a value or of a lock: „Firma „BLP““, „Design „Atelier““, „Standard“. */
function caption(layer: AccountConfigLayerRef): string {
  const name = active.value?.layerName?.(layer);
  switch (layer.subjectType) {
    case "platform":
      return t("layer.platform");
    case "tenant":
      return name ? t("layer.tenantNamed", { name }) : t("layer.tenant");
    case "company":
      return name ? t("layer.companyNamed", { name }) : t("layer.company");
    case "appearance": {
      const shown = name ?? layer.appearance;
      return shown
        ? t("layer.appearanceNamed", { name: shown })
        : t("layer.appearance");
    }
    case "account":
      return name ? t("layer.profileNamed", { name }) : t("layer.account");
    case "account_profile":
      return name ? t("layer.profileNamed", { name }) : t("layer.profile");
    case "account_application":
      return t("layer.application");
    case "account_device":
      return t("layer.device");
    default:
      return layer.subjectType;
  }
}

const declaredRows = computed(() => {
  const current = active.value;
  if (!current) return [];
  return connectSettingsListSections(visible.value, {
    view: (key) => current.store.view(key),
    locking: current.locking === true,
    readOnly: Boolean(current.readOnly),
    appearanceOptions: current.appearanceOptions?.value ?? [],
    caption,
    keybindingTaken: (field) => t("keybindingTaken", { field }),
    keybindingGlobalSearch: t("keybindingGlobalSearch"),
  });
});

/** The host's sections around the declared ones. */
const rows = computed<NxSettingsListSection[]>(() => [
  ...props.leadingSections,
  ...declaredRows.value,
  ...props.trailingSections,
]);

const scopeOptions = computed(() =>
  props.scopes.map((item) => ({
    value: item.id,
    label: item.disabled ? `${item.label} · ${item.disabled}` : item.label,
    disabled: Boolean(item.disabled),
  })),
);

const scopeModel = computed<string | null>({
  get: () => active.value?.id ?? null,
  set: (id) => {
    if (typeof id === "string" && props.scopes.some((item) => item.id === id && !item.disabled)) scope.value = id;
  },
});

const showSwitch = computed(() => props.scopes.length > 1);

function onChange(id: string, value: unknown): void {
  const field = fields.value.get(id);
  const store = active.value?.store;
  if (!field || !store) return;
  store.set(field.key, field.path, connectSettingsStoredValue(field, value));
}

function onReset(id: string): void {
  const field = fields.value.get(id);
  if (field) active.value?.store.set(field.key, field.path, null);
}

function onLock(id: string, locked: boolean): void {
  const field = fields.value.get(id);
  if (!field) return;
  active.value?.store.lock?.(
    field.key,
    connectSettingsLockField(field.path),
    locked,
  );
}

function linkOf(id: string): { label: string; to: string } | undefined {
  return fields.value.get(id)?.link;
}

function sectionLink(id: string): { label: string; to: string } | undefined {
  return visible.value.find((item) => item.id === id)?.link;
}

/**
 * A section's per-object overrides on the edited layer (one table's own
 * settings, I-2510): how many objects the layer overrides, the sentence that
 * says so; null when the section has none or the layer overrides nothing.
 */
function overridesOf(
  id: string,
): { count: number; label: string; key: string; field: string } | null {
  const declared = visible.value.find((item) => item.id === id)?.objectOverrides;
  const view = declared ? active.value?.store.view(declared.key) : null;
  const map = view?.own[declared?.field ?? ""];
  if (!declared || !map || typeof map !== "object" || Array.isArray(map)) return null;
  const count = Object.values(map).filter(
    (entry) => entry && typeof entry === "object" && Object.keys(entry).length > 0,
  ).length;
  if (!count) return null;
  return {
    count,
    label: declared.countLabel.replace("{count}", String(count)),
    key: declared.key,
    field: declared.field,
  };
}

/** „Überall übernehmen": the edited layer's per-object overrides go. */
function applyEverywhere(id: string): void {
  const overrides = overridesOf(id);
  if (overrides) active.value?.store.set(overrides.key, [overrides.field], null);
}

function follow(to: string): void {
  void router?.push(to);
}
</script>

<template>
  <!-- kit-exempt: binds app declarations, authorised config layers, folder-picker data and stores; NxSettingsList owns layout, controls, origins, resets and locks -->
  <NxSettingsList
    v-model:section="section"
    v-model:search="search"
    v-model:changed-only="changedOnly"
    class="connect-settings-list"
    :sections="rows"
    :compact="compact"
    :presentation="presentation"
    :item-label="itemLabel"
    :height="height"
    :disabled="Boolean(active?.readOnly)"
    :search-placeholder="searchPlaceholder"
    data-testid="connect-settings-list"
    @change="onChange"
    @reset="onReset"
    @lock="onLock"
  >
    <template #toolbar>
      <template v-if="showSwitch">
        <NxSettingsField :label="scopeLabel || t('editFor')">
          <NxSingleSelect
            :id="switchId"
            v-model="scopeModel"
            class="connect-settings-list-scope"
            :height="height"
            :placeholder="scopeLabel || t('editFor')"
            :options="scopeOptions.map((item) => item.value)"
            :option-text="(id) => scopeOptions.find((item) => item.value === id)?.label ?? id"
            :option-disabled="(id) => scopeOptions.find((item) => item.value === id)?.disabled ?? false"
            data-testid="settings-scope"
          />
        </NxSettingsField>
      </template>
      <span
        v-else-if="showScope && active"
        class="connect-settings-list-scope-label"
        data-testid="settings-scope-label"
      >
        {{ scopeLabel ? `${scopeLabel}: ${active.label}` : t("editing", { scope: active.label }) }}
      </span>
    </template>
    <template #notice>
      <NxBanner
        v-if="active?.readOnly"
        variant="info"
        data-testid="settings-scope-read-only"
      >
        {{ active.readOnly }}
      </NxBanner>
      <NxBanner
        v-if="active?.store.error.value"
        variant="danger"
        role="alert"
        data-testid="settings-scope-error"
      >
        {{ active.store.error.value }}
      </NxBanner>
      <slot name="notice" />
    </template>
    <template v-if="$slots.content" #content="shown">
      <slot name="content" v-bind="shown" />
    </template>
    <template v-if="$slots['section-end']" #section-end="shown">
      <slot name="section-end" v-bind="shown" />
    </template>
    <template #section-after="{ section: shown }">
      <div
        v-if="overridesOf(shown.id)"
        class="connect-settings-list-overrides"
        :data-testid="`settings-section-overrides-${shown.id}`"
      >
        <span>{{ overridesOf(shown.id)!.label }}</span>
        <NxButton
          height="xs"
          variant="outline"
          :disabled="Boolean(active?.readOnly)"
          :data-testid="`settings-section-apply-everywhere-${shown.id}`"
          @click="applyEverywhere(shown.id)"
        >
          {{ t("applyEverywhere") }}
        </NxButton>
      </div>
      <NxButton
        v-if="sectionLink(shown.id)"
        class="connect-settings-list-link"
        height="xs"
        variant="outline"
        :data-testid="`settings-section-link-${shown.id}`"
        @click="follow(sectionLink(shown.id)!.to)"
      >
        {{ sectionLink(shown.id)!.label }}
      </NxButton>
    </template>
    <template #item-control="{ item, controlId, editable, height: controlHeight, onChange: change }">
      <DriveFolderSetting
        v-if="fields.get(item.id)?.control === 'drive-folder'"
        :key="`${active?.id}:${item.id}`"
        :value="item.value"
        :control-id="controlId"
        :editable="editable"
        :height="controlHeight"
        :permission="fields.get(item.id)?.folderPermission ?? 'read'"
        :folder="folder"
        @change="change"
      />
    </template>
    <template #item-after="{ item }">
      <NxButton
        v-if="linkOf(item.id)"
        class="connect-settings-list-link"
        height="xs"
        variant="outline"
        :data-testid="`settings-link-${item.id}`"
        @click="follow(linkOf(item.id)!.to)"
      >
        {{ linkOf(item.id)!.label }}
      </NxButton>
    </template>
  </NxSettingsList>
</template>

<style scoped>
.connect-settings-list-scope-label {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}

.connect-settings-list-scope {
  min-width: 0;
  max-width: 100%;
}

.connect-settings-list-link {
  align-self: flex-start;
}

.connect-settings-list-overrides {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
}
</style>
